import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ArtworkGrid } from '../components/artwork';
import { ArtworkLightbox, ErrorBoundary, LoadingSpinner } from '../components/ui';
import type { ArtworkResponse, ExhibitionResponse } from '../services';
import { exhibitionService } from '../services';
import { ArtworkObjectImage } from '../components/artwork/ArtworkObjectImage';

const Page = styled.div`
  min-height: 100%;
  padding: clamp(2rem, 5vw, 4.5rem) 0;
`;

const Header = styled.header`
  max-width: 760px;
  margin: 0 auto clamp(2rem, 5vw, 4rem);
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: clamp(2.2rem, 5vw, 4rem);
  line-height: 1.05;
`;

const Subtitle = styled.p`
  min-width: 0;
  max-width: 42rem;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 1.05rem;
  line-height: 1.65;
  overflow-wrap: anywhere;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr));
  gap: clamp(1rem, 2.4vw, 1.75rem);
`;

const CollectionCard = styled(Link)`
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: inherit;
  text-decoration: none;
  transition: transform ${({ theme }) => theme.transitions.gallery.hover}, border-color ${({ theme }) => theme.transitions.gallery.hover};

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.gallery.accent.border};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 4px;
  }
`;

const Preview = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3px;
  aspect-ratio: 16 / 9;
  padding: 3px;
  background: ${({ theme }) => theme.colors.gallery.mat};
`;

const PreviewImage = styled(ArtworkObjectImage)`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  object-fit: contain;
  background: ${({ theme }) => theme.colors.gallery.mat};
`;

const EmptyPreview = styled.div`
  display: grid;
  place-items: center;
  grid-column: 1 / -1;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: 0.9rem;
  text-align: center;
`;

const PreviewUnavailable = styled.div`
  display: grid;
  place-items: center;
  grid-column: 1 / -1;
  gap: 0.45rem;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: 0.9rem;
  text-align: center;
`;

const CardBody = styled.div`
  display: grid;
  min-width: 0;
  gap: 0.75rem;
  padding: 1rem 1.1rem 1.2rem;
`;

const CardTitle = styled.h2`
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: 1.25rem;
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const CardDescription = styled.p`
  min-width: 0;
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow-wrap: anywhere;
`;

const CardMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 0.9rem;
  overflow-wrap: anywhere;
`;

const TagList = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const Tag = styled.span`
  min-width: 0;
  max-width: 100%;
  padding: 0.2rem 0.55rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 0.78rem;
  overflow-wrap: anywhere;
  white-space: normal;
`;

const State = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.8rem;
  max-width: 38rem;
  margin: 2rem auto;
  padding: 2.5rem 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const StateTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: 1.35rem;
`;

const StateMessage = styled.p`
  max-width: 32rem;
  margin: 0;
  line-height: 1.6;
`;

const StateAction = styled.button`
  min-height: 44px;
  padding: 0.6rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.strong};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  }
`;

const StateLink = styled(Link)`
  min-height: 44px;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gallery.accent.primary};
  color: ${({ theme }) => theme.colors.gallery.text.inverse};
  text-decoration: none;
  line-height: 1.4;
`;

const SelectedHeader = styled.section`
  min-width: 0;
  display: grid;
  gap: 1rem;
  margin-bottom: clamp(1.5rem, 4vw, 3rem);
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
`;

const BackLink = styled(Link)`
  justify-self: start;
  min-height: 44px;
  padding: 0.6rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  text-decoration: none;
  line-height: 1.4;
`;

const SelectedTitle = styled.h1`
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: clamp(2rem, 5vw, 3.5rem);
  line-height: 1.08;
  overflow-wrap: anywhere;
`;

const Updating = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  text-align: right;
`;

function parseSelectedId(value: string | null): number | null {
  if (!value || !/^[1-9]\d*$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function artworkCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'artwork' : 'artworks'}`;
}

function ExhibitionThumbnail({ exhibition }: { exhibition: ExhibitionResponse }) {
  const query = useQuery({
    queryKey: ['exhibition-artworks-preview', exhibition.exhibition_id],
    queryFn: () => exhibitionService.getExhibitionArtworks(exhibition.exhibition_id, 4),
    enabled: exhibition.exhibition_id > 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: 0,
    retry: false,
  });
  const publicDataReady = query.isFetchedAfterMount && query.isSuccess && !query.isError && !query.isRefetching && query.fetchStatus !== 'paused';
  const artworks = publicDataReady ? (query.data ?? []) : [];

  if (!publicDataReady && query.isFetching) return <EmptyPreview aria-label="Loading collection preview">Loading preview…</EmptyPreview>;
  if (query.isError && !query.isFetching) {
    return (
      <PreviewUnavailable role="status">
        Preview unavailable
      </PreviewUnavailable>
    );
  }
  if (!publicDataReady) return <EmptyPreview aria-label="Loading collection preview">Loading preview…</EmptyPreview>;
  if (artworks.length === 0) return <EmptyPreview>No works yet</EmptyPreview>;

  return (
    <>
      {artworks.slice(0, 4).map((artwork) => (
        <PreviewImage
          key={artwork.artwork_id}
          artworkId={artwork.artwork_id}
          variant="thumbnail"
          updatedAt={artwork.updated_at}
          alt=""
        />
      ))}
      {Array.from({ length: Math.max(0, 4 - artworks.length) }).map((_, index) => (
        <div key={`empty-${index}`} aria-hidden="true" />
      ))}
    </>
  );
}

function CollectionCardView({ exhibition }: { exhibition: ExhibitionResponse }) {
  const count = exhibition.artwork_count ?? 0;
  return (
    <CollectionCard to={`/exhibitions?selected=${exhibition.exhibition_id}`}>
      <Preview>
        <ExhibitionThumbnail exhibition={exhibition} />
      </Preview>
      <CardBody>
        <CardTitle>{exhibition.title}</CardTitle>
        {exhibition.description && <CardDescription>{exhibition.description}</CardDescription>}
        {exhibition.tags && exhibition.tags.length > 0 && (
          <TagList aria-label="Collection tags">
            {exhibition.tags.slice(0, 4).map((tag) => <Tag key={tag}>{tag}</Tag>)}
            {exhibition.tags.length > 4 && <Tag>+{exhibition.tags.length - 4} more</Tag>}
          </TagList>
        )}
        <CardMeta><span>{artworkCountLabel(count)}</span></CardMeta>
      </CardBody>
    </CollectionCard>
  );
}

export function ExhibitionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedParam = searchParams.get('selected');
  const selectedId = useMemo(() => parseSelectedId(selectedParam), [selectedParam]);
  const hasInvalidSelection = selectedParam !== null && selectedId === null;
  const lightboxOpenerRef = useRef<HTMLElement | null>(null);
  const [lightboxArtwork, setLightboxArtwork] = useState<ArtworkResponse | null>(null);
  const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
  const [artworksForLightbox, setArtworksForLightbox] = useState<ArtworkResponse[]>([]);

  const exhibitionsQuery = useInfiniteQuery({
    queryKey: ['exhibitions'],
    queryFn: ({ pageParam = 1 }) => exhibitionService.getExhibitions(pageParam, 20, undefined, true, false),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: 0,
  });
  const hasExhibitionPageError = exhibitionsQuery.isFetchNextPageError;
  const publicExhibitionsReady = exhibitionsQuery.isFetchedAfterMount && exhibitionsQuery.data !== undefined &&
    exhibitionsQuery.fetchStatus !== 'paused' && !exhibitionsQuery.isRefetching && !exhibitionsQuery.isRefetchError &&
    (exhibitionsQuery.isSuccess || hasExhibitionPageError);
  const exhibitions = useMemo(
    () => publicExhibitionsReady
      ? (exhibitionsQuery.data?.pages.flatMap((page) => page.exhibitions) ?? [])
      : [],
    [exhibitionsQuery.data, publicExhibitionsReady],
  );

  const selectedQuery = useQuery({
    queryKey: ['exhibition', selectedId],
    queryFn: () => exhibitionService.getExhibition(selectedId as number, true),
    enabled: selectedId !== null,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: 0,
  });
  const selectedDataReady = selectedQuery.isFetchedAfterMount && selectedQuery.data !== undefined && selectedQuery.isSuccess &&
    !selectedQuery.isError && !selectedQuery.isRefetchError && !selectedQuery.isFetching && selectedQuery.fetchStatus !== 'paused';
  const artworkQuery = useInfiniteQuery({
    queryKey: ['exhibition-artworks', selectedId],
    queryFn: ({ pageParam = 1 }) => exhibitionService.getExhibitionArtworkPage(selectedId as number, pageParam, 20),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: selectedId !== null && selectedDataReady,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: 0,
  });
  const hasSelectedArtworkPageError = artworkQuery.isFetchNextPageError;
  const selectedArtworksReady = selectedDataReady && artworkQuery.isFetchedAfterMount && artworkQuery.data !== undefined &&
    artworkQuery.fetchStatus !== 'paused' && !artworkQuery.isRefetching && !artworkQuery.isRefetchError &&
    (artworkQuery.isSuccess || hasSelectedArtworkPageError);
  const selectedArtworks = useMemo(
    () => selectedArtworksReady ? (artworkQuery.data?.pages.flatMap((page) => page.artworks) ?? []) : [],
    [artworkQuery.data, selectedArtworksReady],
  );
  const selectedTotal = selectedArtworksReady
    ? (artworkQuery.data?.pages[0]?.total ?? selectedQuery.data?.artwork_count ?? 0)
    : 0;

  useEffect(() => {
    if (!lightboxArtwork) return;
    if (!selectedDataReady || !selectedArtworksReady) {
      setLightboxArtwork(null);
      setArtworksForLightbox([]);
      return;
    }
    const nextIndex = selectedArtworks.findIndex((item) => item.artwork_id === lightboxArtwork.artwork_id);
    if (nextIndex < 0) {
      setLightboxArtwork(null);
      setArtworksForLightbox([]);
      return;
    }
    if (artworksForLightbox !== selectedArtworks) setArtworksForLightbox(selectedArtworks);
    if (nextIndex !== currentArtworkIndex) setCurrentArtworkIndex(nextIndex);
    if (selectedArtworks[nextIndex] !== lightboxArtwork) setLightboxArtwork(selectedArtworks[nextIndex]);
  }, [artworksForLightbox, currentArtworkIndex, lightboxArtwork, selectedArtworks, selectedArtworksReady, selectedDataReady]);

  useEffect(() => {
    setLightboxArtwork(null);
    setArtworksForLightbox([]);
  }, [selectedId]);

  const clearSelection = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('selected');
    setSearchParams(nextParams);
  };

  const handleArtworkClick = (artwork: ArtworkResponse, activatingElement?: HTMLButtonElement) => {
    const index = selectedArtworks.findIndex((item) => item.artwork_id === artwork.artwork_id);
    if (index < 0) return;
    lightboxOpenerRef.current = activatingElement ?? (document.activeElement as HTMLElement | null);
    setArtworksForLightbox(selectedArtworks);
    setCurrentArtworkIndex(index);
    setLightboxArtwork(artwork);
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    const currentIndex = artworksForLightbox.findIndex((item) => item.artwork_id === lightboxArtwork?.artwork_id);
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= artworksForLightbox.length) return;
    setCurrentArtworkIndex(nextIndex);
    setLightboxArtwork(artworksForLightbox[nextIndex]);
  };

  const closeLightbox = () => {
    setLightboxArtwork(null);
    setArtworksForLightbox([]);
  };

  if (selectedId !== null) {
    if (!selectedDataReady && (selectedQuery.isFetching || selectedQuery.fetchStatus === 'paused')) {
      return <Page><State><LoadingSpinner size="lg" text="Loading collection…" /></State></Page>;
    }
    if (selectedQuery.isError || !selectedDataReady || !selectedQuery.data) {
      return (
        <Page>
          <State role="alert">
            <StateTitle>Collection unavailable</StateTitle>
            <StateMessage>This collection could not be loaded. It may have been removed or is temporarily unavailable.</StateMessage>
            <StateLink to="/exhibitions">View collections</StateLink>
          </State>
        </Page>
      );
    }

    const selected = selectedQuery.data;
    return (
      <ErrorBoundary>
        <Page>
          <div className="container">
            <SelectedHeader>
              <BackLink to="/exhibitions">← Back to Collections</BackLink>
              <SelectedTitle>{selected.title}</SelectedTitle>
              {selected.description && <Subtitle>{selected.description}</Subtitle>}
              <CardMeta><span>{artworkCountLabel(selectedTotal)}</span></CardMeta>
              {selected.tags && selected.tags.length > 0 && (
                <TagList aria-label="Collection tags">
                  {selected.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                </TagList>
              )}
            </SelectedHeader>
            <ArtworkGrid
              artworks={selectedArtworks}
              totalCount={selectedTotal}
              isLoading={!selectedArtworksReady && (artworkQuery.isFetching || artworkQuery.fetchStatus === 'paused')}
              isError={(artworkQuery.isError || artworkQuery.isRefetchError) && !artworkQuery.isFetching}
              error={artworkQuery.error}
              onRetry={async () => { await artworkQuery.refetch(); }}
              hasNextPage={Boolean(artworkQuery.hasNextPage)}
              isFetchingNextPage={artworkQuery.isFetchingNextPage}
              isFetchNextPageError={artworkQuery.isFetchNextPageError}
              isFetching={artworkQuery.isFetching}
              onLoadMore={async () => { await artworkQuery.fetchNextPage(); }}
              columns={4}
              aspectRatio={4 / 5}
              onArtworkClick={handleArtworkClick}
            />
            <ArtworkLightbox
              artwork={lightboxArtwork}
              artworks={artworksForLightbox}
              currentIndex={currentArtworkIndex}
              isOpen={lightboxArtwork !== null}
              onClose={closeLightbox}
              onNavigate={navigateLightbox}
              activatingElement={lightboxOpenerRef.current}
              galleryMode
            />
          </div>
        </Page>
      </ErrorBoundary>
    );
  }

  if (hasInvalidSelection) {
    return (
      <Page>
        <State role="alert">
          <StateTitle>Collection link unavailable</StateTitle>
          <StateMessage>The selected collection link is not valid.</StateMessage>
          <StateAction type="button" onClick={clearSelection}>View collections</StateAction>
        </State>
      </Page>
    );
  }

  const listError = exhibitionsQuery.error;
  const collectionRefreshFailed = exhibitionsQuery.isRefetchError;
  const retryCollections = () => exhibitionsQuery.isFetchNextPageError
    ? exhibitionsQuery.fetchNextPage()
    : exhibitionsQuery.refetch();
  return (
    <ErrorBoundary>
      <Page>
        <div className="container">
          <Header>
            <Title>Collections</Title>
            <Subtitle>Browse student work gathered from Joanne Art Class. Each collection reflects the existing studio tags and artwork.</Subtitle>
          </Header>
          {!publicExhibitionsReady && (exhibitionsQuery.isLoading || exhibitionsQuery.isFetching || exhibitionsQuery.fetchStatus === 'paused') && exhibitions.length === 0 && (
            <State><LoadingSpinner size="lg" text="Loading collections…" /></State>
          )}
          {listError && exhibitions.length === 0 && (
            <State role="alert">
              <StateTitle>{collectionRefreshFailed ? 'More collections are unavailable' : 'Collections unavailable'}</StateTitle>
              <StateMessage>We could not load the collections right now.</StateMessage>
              <StateAction type="button" onClick={() => void exhibitionsQuery.refetch()}>Try again</StateAction>
            </State>
          )}
          {publicExhibitionsReady && !exhibitionsQuery.isLoading && !listError && exhibitions.length === 0 && (
            <State>
              <StateTitle>No collections yet</StateTitle>
              <StateMessage>There are no public collections to display yet.</StateMessage>
            </State>
          )}
          {exhibitions.length > 0 && (
            <>
              {exhibitionsQuery.isFetching && !exhibitionsQuery.isFetchingNextPage && <Updating>Updating collections…</Updating>}
              <Grid>
                {exhibitions.map((exhibition) => <CollectionCardView key={exhibition.exhibition_id} exhibition={exhibition} />)}
              </Grid>
              {exhibitionsQuery.hasNextPage && (
                <StateAction
                  type="button"
                  onClick={() => void exhibitionsQuery.fetchNextPage()}
                  disabled={exhibitionsQuery.isFetchingNextPage}
                >
                  {exhibitionsQuery.isFetchingNextPage ? 'Loading collections…' : 'Load more collections'}
                </StateAction>
              )}
              {listError && <State role="alert"><StateTitle>More collections are unavailable</StateTitle><StateAction type="button" onClick={() => void retryCollections()}>Try again</StateAction></State>}
            </>
          )}
        </div>
      </Page>
    </ErrorBoundary>
  );
}
